import transport from "core/model/api/transport";
import { SealdIdentityCheck, TokenInvalidReason } from "core/types";
import jwtDecode from "jwt-decode";

type VerifyTokenValidResponse = { email: string; valid: true };
type VerifyTokenInvalidResponse = {
  error: { reason: TokenInvalidReason };
  valid: false;
};
type TokenVerificationResponse =
  | VerifyTokenValidResponse
  | VerifyTokenInvalidResponse;

export default {
  authenticate({
    password_hashed,
    salt,
    user,
  }: {
    password_hashed: string | undefined;
    salt: string;
    user: string;
  }): Promise<{ expiration: number; token: string }> {
    return transport
      .post<{ id_token: string }>({
        body: {
          password_hashed,
          salt,
          user,
        },
        options: {
          routeName: "authenticate",
        },
        queryParams: null,
        route: "/auth/check",
      })
      .then((res) => {
        const decoded = jwtDecode<any>(res.id_token);
        if (res.id_token) {
          localStorage.setItem("id_token", res.id_token);
        }

        return {
          expiration: decoded.exp,
          token: res.id_token,
        };
      });
  },
  changePassword({
    password_hashed,
    salt,
    timestamp,
    token,
    user,
  }: {
    password_hashed: string | undefined;
    salt: string | undefined;
    timestamp: string;
    token: string;
    user: string;
  }): Promise<Record<string, any>> {
    return transport.post({
      body: {
        password_hashed,
        salt,
        user,
      },
      queryParams: {
        routeName: "changePassword",
        timestamp,
      },
      route: `/reset/change/${token}`,
    });
  },
  checkIdentity(token: string): Promise<SealdIdentityCheck> {
    return transport.post({
      body: {},
      options: {
        routeName: "checkIdentity",
        token,
      },
      queryParams: null,
      route: `/seald/identity/check`,
    });
  },
  checkToken(): Promise<void> {
    return transport.get({
      options: {
        routeName: "checkToken",
      },
      queryParams: null,
      route: `/auth/check_token`,
    });
  },
  getSalt({ user }: { user: string }): Promise<{ salt: string }> {
    return transport.get({
      options: {
        routeName: "authSalt",
      },
      queryParams: null,
      route: `/auth/salt/${user}`,
    });
  },
  inAppChangePassword({
    id,
    new_password_hashed,
    old_password_hashed,
    salt,
    timestamp,
  }: {
    id: number;
    new_password_hashed: string | undefined;
    old_password_hashed: string | undefined;
    salt: string | undefined;
    timestamp: string;
  }): Promise<any> {
    return transport.post({
      body: {
        new_password_hashed,
        old_password_hashed,
        salt,
      },
      options: {
        routeName: "inAppChangePassword",
      },
      queryParams: { timestamp },
      route: `/change/${id}`,
    });
  },
  login({
    activateSeald,
    device,
    subscription_info,
    token,
  }: {
    activateSeald?: boolean;
    device?: { device_id: string; device_type: string };
    subscription_info?: PushSubscription | null;
    token: string;
  }): Promise<any> {
    return transport.post({
      body: {
        ...(device ?? {}),
        subscription_info,
      },
      options: {
        routeName: "login",
        token,
      },
      queryParams: activateSeald ? { seald: true } : null,
      route: "/auth/login",
    });
  },
  logout(): Promise<null | {
    sso_logout_url: string;
    idp_logout_url?: string;
  }> {
    return transport.post({
      body: {},
      options: {
        routeName: "logout",
      },
      queryParams: null,
      route: "/auth/logout",
    });
  },
  oauthCheck({
    careseeker_ids,
    email,
    is_user_manager,
    redirect_to_url,
  }: {
    careseeker_ids?: number[];
    email: string;
    is_user_manager?: boolean;
    redirect_to_url?: string;
  }): Promise<{ url: string } | null> {
    return transport.post({
      body: {
        careseeker_ids,
        is_user_manager,
        redirect_to_url,
        user: email,
      },
      options: {
        routeName: "oauthCheck",
      },
      queryParams: null,
      route: `/oauth-check`,
    });
  },
  resetIdentity(token: string): Promise<{
    seald_database_key: string;
    seald_user_license_token: string;
  }> {
    return transport.post({
      body: {},
      options: {
        routeName: "resetIdentity",
        token,
      },
      queryParams: null,
      route: `/seald/identity/reset`,
    });
  },
  resetPassword(email: string, app: number): Promise<any> {
    return transport.post({
      body: { app, email },
      queryParams: { routeName: "passwordReset" },
      route: `/reset/initiate`,
    });
  },
  sendChallenge2MR(
    token: string,
    queryParams: Record<string, any> | null = null,
  ): Promise<{
    must_authenticate: boolean;
    seald_two_man_rule_key: string;
    seald_two_man_rule_session_id: string;
  }> {
    return transport.post({
      body: {},
      options: {
        routeName: "sendChallenge2MR",
        token,
      },
      queryParams,
      route: `/seald/sendchallenge`,
    });
  },
  testEnvPasswordCheck({ password }: { password: string }): Promise<void> {
    return transport.post({
      body: { password },
      options: {
        routeName: "testEnvPasswordCheck",
      },
      queryParams: null,
      route: `/auth/testenv`,
    });
  },
  verifyToken(token: string): Promise<TokenVerificationResponse> {
    return transport.get({
      options: { routeName: "verifyToken" },
      queryParams: {},
      route: `/reset/verify/${token}?v2=true`,
    });
  },
};
